(function() {
  'use strict';

  const whenVisible = window.theme?.whenVisible || ((element, callback) => callback(element));

  if (customElements.get('collections-show-v2-section')) return;

  class CollectionsShowV2Section extends HTMLElement {
    constructor() {
      super();
      this._swiper = null;
      this._swiperPromise = null;
      this._resizeFrame = null;
      this._lastWidth = window.innerWidth;
      this._handleResize = this._handleResize.bind(this);
      this._onResize = this._onResize.bind(this);
    }

    connectedCallback() {
      this._swiperElement = this.querySelector('.collections-show-v2__swiper');
      this._navElement = this.querySelector('.collections-show-v2__nav');
      this._paginationElement = this.querySelector('.collections-show-v2__pagination');
      if (!this._swiperElement) return;

      window.addEventListener('resize', this._onResize, { passive: true });
      this._visibilityObserver = whenVisible(this, () => this._handleResize());
    }

    disconnectedCallback() {
      this._visibilityObserver?.disconnect?.();
      window.removeEventListener('resize', this._onResize);

      if (this._resizeFrame) {
        cancelAnimationFrame(this._resizeFrame);
        this._resizeFrame = null;
      }

      this._destroySwiper();
    }

    _onResize() {
      // Ignore height-only resizes (e.g. mobile address bar show/hide). With
      // loop enabled, calling swiper.update() runs loopFix() which can shift
      // the active slide, making the carousel appear to auto-scroll on scroll.
      const width = window.innerWidth;
      if (width === this._lastWidth) return;
      this._lastWidth = width;

      this._handleResize();
    }

    _handleResize() {
      if (this._resizeFrame) cancelAnimationFrame(this._resizeFrame);

      this._resizeFrame = requestAnimationFrame(() => {
        this._resizeFrame = null;

        if (!this.isConnected || !this._swiperElement) return;

        const navigationVisible = this._shouldShowNavigation();
        this._updateControls(navigationVisible);

        if (this._shouldStackMobile() || !this._hasOverflow()) {
          this._destroySwiper();
          return;
        }

        if (this._swiper) {
          this._swiper.update();
          return;
        }

        this._ensureSwiper();
      });
    }

    _shouldStackMobile() {
      const stackMobile = this.dataset.stackMobile === 'true';
      const mobileBreakpoint = parseInt(this.dataset.mobileBreakpoint, 10) || 640;

      return stackMobile && window.innerWidth <= mobileBreakpoint;
    }

    _getSlidesPerView() {
      const slidesDesktop = parseFloat(this.dataset.slidesDesktop) || 3;
      const slidesTablet = parseFloat(this.dataset.slidesTablet) || 2;
      const slidesMobile = parseFloat(this.dataset.slidesMobile) || 1;

      if (window.innerWidth <= 640) return slidesMobile;
      if (window.innerWidth <= 1024) return slidesTablet;
      return slidesDesktop;
    }

    _getSlideCount() {
      return this._swiperElement?.querySelectorAll('.swiper-slide').length || 0;
    }

    _hasOverflow() {
      return this._getSlideCount() > Math.ceil(this._getSlidesPerView());
    }

    _shouldShowNavigation() {
      return this.dataset.showNavigation === 'true' && !this._shouldStackMobile() && this._hasOverflow();
    }

    _shouldShowPagination() {
      return this.dataset.showDots === 'true' && !this._shouldStackMobile() && this._hasOverflow();
    }

    _updateControls(navigationVisible) {
      const paginationVisible = this._shouldShowPagination();

      this.classList.toggle('collections-show-v2--nav-hidden', !navigationVisible);

      if (this._navElement) {
        this._navElement.hidden = !navigationVisible;
      }

      if (this._paginationElement) {
        this._paginationElement.hidden = !paginationVisible;
      }
    }

    _ensureSwiper() {
      if (this._swiper || this._swiperPromise || typeof window.loadThemeSwiper !== 'function') return;

      this._swiperPromise = window.loadThemeSwiper()
        .then(() => {
          this._swiperPromise = null;

          if (!this.isConnected || this._shouldStackMobile() || !this._hasOverflow()) return;

          this._createSwiper();
        })
        .catch((error) => {
          this._swiperPromise = null;
          console.warn('Swiper failed to load for CollectionsShowV2:', error);
        });
    }

    _createSwiper() {
      this._destroySwiper();

      const slidesDesktop = parseFloat(this.dataset.slidesDesktop) || 3;
      const slidesTablet = parseFloat(this.dataset.slidesTablet) || 2;
      const slidesMobile = parseFloat(this.dataset.slidesMobile) || 1;
      const spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 16;
      const spaceBetweenMobile = parseInt(this.dataset.spaceBetweenMobile, 10) || 12;
      const speed = parseInt(this.dataset.speed, 10) || 350;
      const showNavigation = this._shouldShowNavigation();
      const showDots = this._shouldShowPagination();
      const slideCount = this._getSlideCount();
      const loopEnabled = this.dataset.loop === 'true';
      const loop = loopEnabled && slideCount > Math.ceil(slidesDesktop);
      const autoplayEnabled = this.dataset.autoplay === 'true';
      const autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 4000;
      const navPrev = this.querySelector('.collections-show-v2__nav-btn--prev');
      const navNext = this.querySelector('.collections-show-v2__nav-btn--next');
      const paginationEl = this._paginationElement;

      this._swiper = new Swiper(this._swiperElement, {
        slidesPerView: slidesDesktop,
        spaceBetween: spaceBetween,
        speed: speed,
        loop: loop,
        autoplay: autoplayEnabled
          ? {
              delay: autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }
          : false,
        watchOverflow: true,
        navigation: showNavigation && navPrev && navNext
          ? {
              prevEl: navPrev,
              nextEl: navNext
            }
          : false,
        pagination: showDots && paginationEl
          ? {
              el: paginationEl,
              clickable: true
            }
          : false,
        breakpoints: {
          0: {
            slidesPerView: slidesMobile,
            spaceBetween: spaceBetweenMobile
          },
          641: {
            slidesPerView: slidesTablet,
            spaceBetween: spaceBetween
          },
          1025: {
            slidesPerView: slidesDesktop,
            spaceBetween: spaceBetween
          }
        }
      });
    }

    _destroySwiper() {
      if (!this._swiper) return;

      this._swiper.destroy(true, true);
      this._swiper = null;
    }
  }

  customElements.define('collections-show-v2-section', CollectionsShowV2Section);
})();
